import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Tooltip, TextField, MenuItem, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';

import * as fileApi from '../../../lib/api/file';

/**
 * 파일 찾기 — 영업이든 제조든 상관없이.
 *
 * 지금까지는 파일이 붙어 있는 곳을 알아야 꺼낼 수 있었다. 이 공정의 파일, 이 계획의 파일처럼.
 * 그래서 "지난달에 올린 그 검사 사진" 같은 걸 찾을 방법이 없었다.
 *
 * 공통관리의 파일 찾기 화면과 채팅의 파일 넣기가 이 컴포넌트를 같이 쓴다.
 * 두 벌로 두면 한쪽만 고쳐진다.
 *
 * @param {Function} [onPick] - 주면 줄마다 [선택] 버튼이 붙는다. 채팅에서 파일을 고를 때 쓴다.
 * @param {string}   [pickLabel] - 그 버튼에 쓸 말
 * @param {Object}   [initialConditions] - 처음부터 채워 넣을 조건. { query, uploader, fileTyp, strtDt, endDt }.
 *   채팅의 FILE_LIST 응답에서 "전체 보기"로 열 때, 봇이 이미 적용한 조건을 그대로 넘겨 받는다.
 */

/** 저장할 때 쓰는 코드를 사람이 읽는 말로. */
const TYPE_LABEL = {
  design: '설계', material: '원자재', production: '생산', process: '공정',
  qc: 'QC', completion: '생산완료', shipment: '출하',
  project: '프로젝트', chat: '채팅', esg: 'ESG', tech: '기술',
  PURCHASE_ORDER: '발주', profile: '프로필',
};

const TYPE_COLOR = {
  process: 'success', shipment: 'error', qc: 'warning',
  project: 'primary', chat: 'info', design: 'info',
};

const TYPE_OPTIONS = ['project', 'process', 'shipment', 'design', 'qc', 'chat', 'esg', 'tech'];

const FileSearchPanel = ({ onPick, pickLabel = '선택', initialConditions }) => {
  const [keyword, setKeyword] = useState(initialConditions?.query || '');
  const [writer, setWriter] = useState(initialConditions?.uploader || '');
  const [fileTyp, setFileTyp] = useState(initialConditions?.fileTyp || '');
  const [strtDt, setStrtDt] = useState(initialConditions?.strtDt || '');
  const [endDt, setEndDt] = useState(initialConditions?.endDt || '');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fileApi.searchFiles({
        searchNm: keyword.trim() || undefined,
        searchCrtNm: writer.trim() || undefined,
        fileTyp: fileTyp || undefined,
        strtDt: strtDt || undefined,
        endDt: endDt || undefined,
      });
      setRows(res.data?.list || []);
      setTotal(res.data?.totalCount || 0);
    } catch (err) {
      setError(err?.response?.data?.message || '파일을 찾지 못했습니다.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [keyword, writer, fileTyp, strtDt, endDt]);

  // 처음 열면 최근 것부터 보여준다. 빈 화면보다 뭐라도 보이는 편이 낫다.
  useEffect(() => { search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleDownload = async (file) => {
    try {
      const res = await fileApi.downloadFile(file.fileUuid);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileNm || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('파일을 내려받지 못했습니다.');
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'action.hover' }}>
        {/* 휴대폰: 2칸 격자 - 파일명(한 줄 전체, 돋보기로 찾기) / 등록자·종류 / 등록일 시작·종료 */}
        <Box
          sx={(theme) => ({
            display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              '& > .MuiTextField-root': { width: '100%', minWidth: 0 },
              '& > .fsp-keyword': { gridColumn: '1 / -1' },
              '& > .fsp-search-btn': { display: 'none' },
            },
          })}
        >
          <TextField
            size="small" label="파일명" value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder="이름 일부만 넣어도 됩니다"
            className="fsp-keyword"
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              // 휴대폰에서는 [찾기] 버튼을 숨기고 오른쪽 끝 버튼으로 찾는다
              endAdornment: (
                <InputAdornment position="end" sx={{ display: { xs: 'flex', sm: 'none' } }}>
                  <Button size="small" variant="contained" onClick={search} disabled={loading} sx={{ minWidth: 0, px: 1.5 }}>
                    찾기
                  </Button>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small" label="등록자" value={writer}
            onChange={(e) => setWriter(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            sx={{ width: 130 }}
          />
          <TextField
            select size="small" label="종류" value={fileTyp}
            onChange={(e) => setFileTyp(e.target.value)}
            sx={{ width: 130 }}
          >
            <MenuItem value="">전체</MenuItem>
            {TYPE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>{TYPE_LABEL[t] || t}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" type="date" label="등록일 시작" value={strtDt}
            onChange={(e) => setStrtDt(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />
          <TextField
            size="small" type="date" label="등록일 종료" value={endDt}
            onChange={(e) => setEndDt(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
          />
          <Button className="fsp-search-btn" variant="contained" size="small" onClick={search} disabled={loading}>
            찾기
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          프로필 사진은 빼고 찾습니다. 종류에서 직접 고르시면 그때는 나옵니다.
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {loading ? '찾는 중…' : `${total}건`}
          {/* 서버가 한 번에 100건까지만 준다. 더 있으면 조건을 좁히라고 알려준다. */}
          {!loading && total > rows.length && ` (${rows.length}건 표시 — 조건을 좁혀보세요)`}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">찾는 조건에 맞는 파일이 없습니다.</Alert>
      ) : (
        <Paper variant="outlined">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 96 }}>종류</TableCell>
                  <TableCell>파일명</TableCell>
                  <TableCell sx={{ width: 200 }}>출처</TableCell>
                  <TableCell sx={{ width: 100 }}>등록자</TableCell>
                  <TableCell sx={{ width: 110 }}>등록일</TableCell>
                  <TableCell sx={{ width: onPick ? 130 : 60 }} align="center">
                    {onPick ? '' : '받기'}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((f) => (
                  <TableRow key={f.fileUuid} hover>
                    <TableCell>
                      <Chip
                        size="small" variant="outlined"
                        label={TYPE_LABEL[f.fileTyp] || f.fileTyp || '-'}
                        color={TYPE_COLOR[f.fileTyp] || 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>
                      {f.fileNm || '-'}
                      {f.fileAttrVal3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          {f.fileAttrVal3}
                        </Typography>
                      )}
                    </TableCell>
                    {/* 파일명만 봐서는 어느 프로젝트 것인지 모른다.
                        제조·영업 파일만 풀 수 있고 나머지는 비어 있다. */}
                    <TableCell>
                      <Typography variant="body2" color={f.sourceNm ? 'text.primary' : 'text.disabled'}>
                        {f.sourceNm || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{f.crtNm || '-'}</TableCell>
                    <TableCell>{String(f.crtDt || '').substring(0, 10) || '-'}</TableCell>
                    <TableCell align="center">
                      {onPick ? (
                        <Button size="small" variant="outlined" onClick={() => onPick(f)}>
                          {pickLabel}
                        </Button>
                      ) : (
                        <Tooltip title="내려받기">
                          <IconButton size="small" onClick={() => handleDownload(f)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default FileSearchPanel;
